import React from 'react';
import { styles } from '../styles';

interface LineIconProps {
  machineCount: number;
  size?: number;
  className?: string;
}

export const LineIcon: React.FC<LineIconProps> = ({ 
  machineCount, 
  size = 20, 
  className = "" 
}) => {
  // Limit the number of bullets to 8 maximum
  const displayCount = Math.min(machineCount, 8);
  
  // Fixed line width for consistent appearance
  const lineWidth = size * 3; // Make line wider for better distribution
  const bulletRadius = Math.max(3, size / 6); // Bigger bullets
  const lineStartX = bulletRadius + 2; // Start line after first bullet
  const lineEndX = lineWidth - bulletRadius - 2; // End line before last bullet
  
  // Calculate bullet positions evenly distributed across the line
  const getBulletPosition = (index: number) => {
    if (displayCount === 1) {
      return lineWidth / 2; // Center single bullet
    }
    // Distribute bullets evenly from start to end
    return lineStartX + (index * (lineEndX - lineStartX)) / (displayCount - 1);
  };
  
  return (
    <svg 
      width={lineWidth} 
      height={size} 
      viewBox={`0 0 ${lineWidth} ${size}`} 
      className={`${styles.lineIcon.container} ${className}`}
      role="img"
      aria-label={`Line with ${machineCount} machines`}
    >
      {/* Main horizontal line */}
      <line
        x1={lineStartX}
        y1={size / 2}
        x2={lineEndX}
        y2={size / 2}
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
      {/* Bullets representing machines - distributed evenly across the line */}
      {displayCount > 0 && Array.from({ length: displayCount }, (_, index) => (
        <circle
          key={index}
          cx={getBulletPosition(index)}
          cy={size / 2}
          r={bulletRadius}
          fill="currentColor"
        />
      ))}
      {/* Show "+X" if there are more machines than bullets displayed */}
      {machineCount > 8 && (
        <text
          x={lineWidth - 5}
          y={size / 2}
          dy="0.35em"
          fontSize={Math.max(8, size / 3)}
          fill="currentColor"
          textAnchor="end"
          className="font-mono font-bold"
        >
          +{machineCount - 8}
        </text>
      )}
    </svg>
  );
};
